import Brand from "@/Components/Base/Brand";
import { CSSProperties, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import {
  A4_HEIGHT_PX,
  A4_WIDTH_PX,
  allergenIcons,
  PosterColors,
  posterAllergens,
  PosterRow,
  withAlpha,
} from "./allergenPosterUtils";

const PAGE_PADDING_X = 36;
const ROW_HEIGHT = 24;
const COLUMN_WIDTH = 32;
const COLUMN_HEADER_HEIGHT = 96;
const TEXT_COLOR = "#1f2933";
const MUTED_COLOR = "#6b7280";
const BORDER_COLOR = "#e5e7eb";
const FONT_FAMILY =
  "'Inter', 'Helvetica Neue', Helvetica, Arial, 'Segoe UI', sans-serif";

export type AllergenPosterBranch = {
  name: string;
  address?: string;
  phone?: string;
};

type AllergenPosterPageProps = {
  rows: PosterRow[];
  pageIndex: number;
  pageCount: number;
  colors: PosterColors;
  title: string;
  branch: AllergenPosterBranch;
  logoUrl?: string | null;
  generatedAt: string;
};

export const AllergenPosterPage = forwardRef<
  HTMLDivElement,
  AllergenPosterPageProps
>(
  (
    {
      rows,
      pageIndex,
      pageCount,
      colors,
      title,
      branch,
      logoUrl,
      generatedAt,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const isFirstPage = pageIndex === 0;
    const columnTint = withAlpha(colors.primary, 0.05);

    const pageStyle: CSSProperties = {
      width: A4_WIDTH_PX,
      height: A4_HEIGHT_PX,
      background: "#ffffff",
      color: TEXT_COLOR,
      fontFamily: FONT_FAMILY,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
      boxSizing: "border-box",
    };

    return (
      <div ref={ref} style={pageStyle}>
        {isFirstPage ? (
          <div
            style={{
              background: colors.primary,
              color: colors.primaryText,
              padding: `28px ${PAGE_PADDING_X}px 24px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 24,
              minHeight: 150,
              boxSizing: "border-box",
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: 2.5,
                  textTransform: "uppercase",
                  opacity: 0.85,
                  fontWeight: 600,
                }}
              >
                {branch.name}
              </div>
              <div
                style={{
                  fontSize: 34,
                  lineHeight: 1.1,
                  fontWeight: 800,
                  marginTop: 8,
                }}
              >
                {title}
              </div>
              {(branch.address || branch.phone) && (
                <div style={{ fontSize: 11, marginTop: 10, opacity: 0.85 }}>
                  {[branch.address, branch.phone].filter(Boolean).join("  ·  ")}
                </div>
              )}
            </div>
            <div
              style={{
                background: "#ffffff",
                borderRadius: 16,
                padding: 14,
                width: 150,
                height: 96,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxSizing: "border-box",
              }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  crossOrigin="anonymous"
                  alt={branch.name}
                  style={{
                    maxWidth: 122,
                    maxHeight: 68,
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Brand style={{ width: 122, height: 26 }} />
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              background: colors.primary,
              color: colors.primaryText,
              padding: `14px ${PAGE_PADDING_X}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <span>{title}</span>
            <span style={{ fontWeight: 500, opacity: 0.85 }}>{branch.name}</span>
          </div>
        )}

        {isFirstPage && (
          <div
            style={{
              margin: `16px ${PAGE_PADDING_X}px 0`,
              padding: "10px 14px",
              borderRadius: 10,
              background: withAlpha(colors.primary, 0.08),
              borderLeft: `4px solid ${colors.accent}`,
              fontSize: 11.5,
              lineHeight: 1.45,
              color: TEXT_COLOR,
            }}
          >
            {t("allergenPoster.intro")}
          </div>
        )}

        <div
          style={{
            flex: 1,
            padding: `16px ${PAGE_PADDING_X}px 0`,
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              height: COLUMN_HEADER_HEIGHT,
              borderBottom: `2px solid ${colors.primary}`,
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: 8,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                color: MUTED_COLOR,
              }}
            >
              {t("allergenPoster.product")}
            </div>
            {posterAllergens.map((allergen, index) => (
              <div
                key={allergen.id}
                style={{
                  width: COLUMN_WIDTH,
                  flexShrink: 0,
                  position: "relative",
                  background: index % 2 === 0 ? columnTint : "transparent",
                  borderTopLeftRadius: 6,
                  borderTopRightRadius: 6,
                }}
              >
                <img
                  src={allergenIcons[allergen.id]}
                  alt=""
                  style={{
                    width: 26,
                    height: 26,
                    position: "absolute",
                    top: 4,
                    left: (COLUMN_WIDTH - 26) / 2,
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    width: 58,
                    left: COLUMN_WIDTH / 2 - 29,
                    bottom: 30,
                    transform: "rotate(-90deg)",
                    fontSize: 8.5,
                    lineHeight: "10px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    textAlign: "left",
                    color: TEXT_COLOR,
                  }}
                >
                  {t(allergen.translationKey)}
                </span>
              </div>
            ))}
          </div>

          {rows.map((row, rowIndex) =>
            row.type === "category" ? (
              <div
                key={`${row.id}-${rowIndex}`}
                style={{
                  height: ROW_HEIGHT,
                  display: "flex",
                  alignItems: "center",
                  marginTop: rowIndex === 0 ? 4 : 0,
                  padding: "0 8px",
                  background: colors.category,
                  color: colors.categoryText,
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  borderRadius: 4,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {row.name}
                {row.isContinued && (
                  <span
                    style={{
                      fontWeight: 500,
                      textTransform: "none",
                      marginLeft: 6,
                      letterSpacing: 0,
                      opacity: 0.8,
                    }}
                  >
                    ({t("allergenPoster.continued")})
                  </span>
                )}
              </div>
            ) : (
              <div
                key={`${row.id}-${rowIndex}`}
                style={{
                  height: ROW_HEIGHT,
                  display: "flex",
                  alignItems: "stretch",
                  borderBottom: `1px solid ${BORDER_COLOR}`,
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 8px",
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {row.name}
                </div>
                {posterAllergens.map((allergen, index) => {
                  const contains = row.allergens.includes(allergen.id);
                  return (
                    <div
                      key={allergen.id}
                      style={{
                        width: COLUMN_WIDTH,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          index % 2 === 0 ? columnTint : "transparent",
                      }}
                    >
                      {contains && (
                        <div
                          style={{
                            width: 15,
                            height: 15,
                            borderRadius: "50%",
                            background: colors.accent,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {/* SVG instead of a ✓ glyph: html2canvas shifts text baselines in the PDF */}
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 12 12"
                            style={{ display: "block" }}
                          >
                            <path
                              d="M2.5 6.4 L5 8.8 L9.6 3.4"
                              fill="none"
                              stroke={colors.accentText}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {!rows.length && (
            <div
              style={{
                padding: "40px 0",
                textAlign: "center",
                fontSize: 13,
                color: MUTED_COLOR,
              }}
            >
              {t("allergenPoster.empty")}
            </div>
          )}
        </div>

        <div
          style={{
            padding: `12px ${PAGE_PADDING_X}px 22px`,
            borderTop: `1px solid ${BORDER_COLOR}`,
            margin: `0 ${PAGE_PADDING_X}px`,
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <div
            style={{
              fontSize: 7,
              lineHeight: 1.45,
              color: MUTED_COLOR,
              textAlign: "justify",
            }}
          >
            <strong style={{ color: TEXT_COLOR }}>
              {t("allergenPoster.disclaimerTitle")}
            </strong>{" "}
            {t("allergenPoster.disclaimer", { branch: branch.name })}
          </div>
          <div
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: 7.5,
              color: MUTED_COLOR,
            }}
          >
            <span>
              {t("allergenPoster.generatedAt", { date: generatedAt })}
            </span>
            <span>
              {t("allergenPoster.page", {
                page: pageIndex + 1,
                total: pageCount,
              })}
            </span>
          </div>
        </div>
      </div>
    );
  }
);

AllergenPosterPage.displayName = "AllergenPosterPage";
